// React e Hooks
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
// Bibliotecas de Terceiros (Libs)
import { toast } from 'react-toastify';
import { isAfter, parseISO } from 'date-fns';
// Ícones
import { AlertCircle, LinkIcon, User, CreditCard, Zap, Receipt, Shield } from 'lucide-react';
import { FaSpinner } from "react-icons/fa6";
import { FaCheck } from "react-icons/fa";
// Componentes da Aplicação
import { TextInput } from '../../Components/Inputs/TextInput';
import { MaskedInputCPF } from '../../Components/Inputs/MaskedCpf';
import { PixModal } from '../../Components/PixModal';
import { BoletoModal } from '../../Components/BoletoModal';
import { CreditCardFields } from '../../Components/CreditCardFields';
import { CardHolderFields } from '../../Components/CardHolderFields';
import { BoletoFields } from '../../Components/BoletoFields';
import { BoletoHolderFields } from '../../Components/BoletoHolderFields';
// Serviços (API Calls)
import { findLinkById, findPaymentMethodByLinkId, createPaymentLinkHistory, findHistoryByLinkId } from '../../Services/LinkPagamentoService';
import { criarPix } from '../../Services/PixService';
import { emitirBoleto } from '../../Services/BoletoService';
import {
    fetchEstadosEMunicipios,
    getMunicipiosByUf,
    LocalizacoesData,
} from '../../Services/LocalizacaoService';
import {
    processarCartao,
    criarCheckoutHosted,
    isTransparentCheckoutAvailable,
    C6SdkUnavailableError,
} from '../../Services/CreditCardService';
// Tipos e Interfaces
import { LinkPagamento, PaymentMethod, IHistoricoPagamento, BoletoTitulo } from '../../Types';
// Utilitários e Helpers
import { formatCurrency, formatDate } from '../../Utils/format';
import { getMethodIcon, getMethodName } from '../../Utils';
import { isCreditCardMethod } from '../../Utils/cardBrand';
import { isBoletoMethod } from '../../Utils/boletoConfig';
import {
    buildCreditCardSubmission,
    buildPagadorCartao,
    validateCreditCardCheckoutForm,
    validatePixPagadorForm,
} from '../../Utils/cardValidation';
import {
    buildBoletoPayload,
    validateBoletoCheckoutForm,
} from '../../Utils/boletoValidation';
import { IMaskInput } from 'react-imask';
import { CHECKOUT_FIELD_LIMITS } from '../../Constants/checkoutFieldLimits';
import { BRAZILIAN_STATES } from '../../Constants/brazilianStates';
import { fetchAddressByCep } from '../../Utils/cepLookup';
import {
    ensureCityInOptions,
    fetchCitiesByUf,
    findCityInList,
} from '../../Utils/fetchCitiesByUf';


/**
 * Decisão hosted × transparente do cartão C6: sem REACT_APP_C6_SDK_URL no .env
 * o checkout transparente não é possível — a UI oculta os campos do cartão e o
 * submit cria um checkout HOSTED (pagador digita o cartão na página do banco).
 * Com a URL configurada, o fluxo transparente permanece o padrão e o hosted é
 * apenas fallback em runtime quando o script do SDK falha ao carregar.
 */
const HOSTED_CARD_MODE = !isTransparentCheckoutAvailable();

const CheckoutPage = () => {
    const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
    const [linkData, setLinkData] = useState<LinkPagamento | null>(null);
    const [empresaData, setEmpresaData] = useState<{ codigoEmpresa: string; cpfCnpj: string; nomeRazaoSocial: string } | null>(null);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [processingPayment, setProcessingPayment] = useState<boolean>(false);
    const [pixData, setPixData] = useState<{ qrCode: string; copiaECola: string } | null>(null);
    const [isPixModalOpen, setIsPixModalOpen] = useState(false);
    const [pixTransactionId, setPixTransactionId] = useState<number | string | null>(null);
    const [pixStreamUrl, setPixStreamUrl] = useState<string | null>(null);
    const [boletoTitulos, setBoletoTitulos] = useState<BoletoTitulo[]>([]);
    const [cardPaymentSuccess, setCardPaymentSuccess] = useState<{
        valorPago: number;
        parcelas: number;
        codigoAutorizacao?: string;
        cartao?: { numeroMascarado: string; bandeira: string };
    } | null>(null);
    const [isBoletoModalOpen, setIsBoletoModalOpen] = useState(false);
    const [isExpired, setIsExpired] = useState(false);

    const [pagadorCpfCnpj, setPagadorCpfCnpj] = useState<string>('');
    const [pagadorNome, setPagadorNome] = useState<string>('');
    const [emailContato, setEmailContato] = useState<string>('');
    const [errors, setErrors] = useState<{[key: string]: string}>({});

    const [cardNumber, setCardNumber] = useState('');
    const [cardHolderName, setCardHolderName] = useState('');
    const [cardExpiry, setCardExpiry] = useState('');
    const [cardCvv, setCardCvv] = useState('');
    const [installments, setInstallments] = useState(1);
    const [holderCep, setHolderCep] = useState('');
    const [holderAddress, setHolderAddress] = useState('');
    const [holderNumber, setHolderNumber] = useState('');
    const [holderNeighborhood, setHolderNeighborhood] = useState('');
    const [holderState, setHolderState] = useState('');
    const [holderCity, setHolderCity] = useState('');
    const [holderComplement, setHolderComplement] = useState('');
    const [holderPhone, setHolderPhone] = useState('');
    const [loadingCep, setLoadingCep] = useState(false);
    const [cityOptions, setCityOptions] = useState<string[]>([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [boletoCityOptions, setBoletoCityOptions] = useState<string[]>([]);
    const [loadingBoletoCities, setLoadingBoletoCities] = useState(false);
    const [localidades, setLocalidades] = useState<LocalizacoesData | null>(null);
    const [loadingLocalidades, setLoadingLocalidades] = useState(false);
    const lastCepFetchedRef = useRef<string>('');
    const localidadesFetchAttemptedRef = useRef(false);

    const [isLimitReached, setIsLimitReached] = useState(false);
    
    useEffect(() => {
        const getUrlParams = () => {
            const params = new URLSearchParams(window.location.search);
            return {
                linkPagamentoId: params.get('linkPagamentoId'),
                authToken: params.get('authToken'),
                empresa: params.get('empresa'),
                filial: params.get('filial')
            };
        };
        
        const fetchData = async () => {
            const { linkPagamentoId, authToken, empresa, filial } = getUrlParams();

            if (!linkPagamentoId) {
                setError('Página inválida: link de pagamento não informado.');
                setLoading(false);
                return;
            }
            if (!empresa?.trim()) {
                setError('Página inválida: parâmetro empresa é obrigatório na URL.');
                setLoading(false);
                return;
            }

            try {
                const result = await findLinkById(authToken ?? '', linkPagamentoId, empresa, filial);

                if (result && result.statusCode === 200 && result.data) {
                    setLinkData(result.data);
                    
                    // validade é opcional no backend (null = link sem expiração)
                    if (result.data.validade) {
                        const dateWithoutZ = result.data.validade.replace('Z', '');
                        const dataValidade = parseISO(dateWithoutZ);
                        const agora = new Date();
                        if (isAfter(agora, dataValidade)) {
                            setIsExpired(true);
                            setLoading(false);
                            return;
                        }
                    }
                    
                    const historyResult = await findHistoryByLinkId(authToken ?? '', linkPagamentoId, undefined, empresa, filial);
                    const paidCount = (historyResult?.data || []).filter(
                        (historyItem: IHistoricoPagamento) => historyItem.statusPagamento.toLowerCase() === 'pago' || historyItem.statusPagamento.toLowerCase() === 'concluido'
                    ).length;

                    const linkLimit = result.data.limitePagamentos;
                    if (linkLimit && paidCount >= linkLimit) {
                        setIsLimitReached(true);
                        setLoading(false);
                        return; 
                    }

                    const methodsResult = await findPaymentMethodByLinkId(authToken ?? '', linkPagamentoId, empresa, filial);
                    
                    if (methodsResult?.data) {
                        const activeMethods = (methodsResult.data.methods || []).filter((method: PaymentMethod) => method.ativo);
                        setPaymentMethods(activeMethods);

                        if (methodsResult.data.empresa) {
                            setEmpresaData(methodsResult.data.empresa);
                        } else {
                            setEmpresaData({
                                codigoEmpresa: '',
                                cpfCnpj: '',
                                nomeRazaoSocial: result.data?.nomeLink || 'Pagamento',
                            });
                        }

                        if (methodsResult.data.logo) {
                            setLogoUrl(methodsResult.data.logo);
                        }
                    } else {
                        setError(methodsResult?.message || "Erro ao carregar métodos de pagamento.");
                    }
                } else {
                    setError(result?.message || 'Link de pagamento não encontrado ou inválido.');
                }
            } catch (err: any) {
                console.error("Erro ao carregar link de pagamento:", err);
                setError(err.message || 'Erro de conexão com o servidor');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const clearFieldError = (name: string) => {
        if (errors[name]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        clearFieldError(name);

        if (name === 'pagadorCpfCnpj') setPagadorCpfCnpj(value);
        else if (name === 'pagadorNome') setPagadorNome(value);
        else if (name === 'emailContato') setEmailContato(value);
        else if (name === 'holderAddress') setHolderAddress(value);
        else if (name === 'holderNumber') setHolderNumber(value);
        else if (name === 'holderNeighborhood') setHolderNeighborhood(value);
    };

    const handleCardFieldChange = (name: string, value: string | number) => {
        clearFieldError(name);
        if (name === 'cardNumber') setCardNumber(String(value));
        else if (name === 'cardHolderName') setCardHolderName(String(value));
        else if (name === 'cardExpiry') setCardExpiry(String(value));
        else if (name === 'cardCvv') setCardCvv(String(value));
        else if (name === 'installments') setInstallments(Number(value));
    };

    const loadLocalidades = useCallback(async () => {
        if (localidadesFetchAttemptedRef.current) return;
        localidadesFetchAttemptedRef.current = true;

        const params = new URLSearchParams(window.location.search);
        const empresa = params.get('empresa');
        const filial = params.get('filial');
        setLoadingLocalidades(true);
        try {
            const data = await fetchEstadosEMunicipios(empresa, filial);
            setLocalidades(data);
        } catch (err: any) {
            toast.error(err?.message || 'Não foi possível carregar estados e municípios.');
        } finally {
            setLoadingLocalidades(false);
        }
    }, []);

    useEffect(() => {
        if (
            selectedMethod &&
            isBoletoMethod(selectedMethod.tipoMetodo) &&
            !localidades &&
            !loadingLocalidades &&
            !localidadesFetchAttemptedRef.current
        ) {
            loadLocalidades();
        }
    }, [selectedMethod, localidades, loadingLocalidades, loadLocalidades]);

    const loadBoletoCitiesForUf = useCallback(
        async (uf: string, preferredCity?: string) => {
            const sigla = uf.trim().toUpperCase();
            if (sigla.length !== 2) {
                setBoletoCityOptions([]);
                return;
            }

            setLoadingBoletoCities(true);
            try {
                let cities: string[] = [];
                if (localidades) {
                    cities = getMunicipiosByUf(localidades.municipios, sigla, localidades.estados).map(
                        (m) => m.nome
                    );
                }
                if (cities.length === 0) {
                    cities = await fetchCitiesByUf(sigla);
                }
                if (preferredCity?.trim()) {
                    cities = ensureCityInOptions(preferredCity, cities);
                    const match = findCityInList(preferredCity, cities) ?? preferredCity.trim();
                    setHolderCity(match);
                }
                setBoletoCityOptions(cities);
                clearFieldError('holderCity');
            } catch {
                if (preferredCity?.trim()) {
                    setBoletoCityOptions([preferredCity.trim()]);
                    setHolderCity(preferredCity.trim());
                } else {
                    setBoletoCityOptions([]);
                }
            } finally {
                setLoadingBoletoCities(false);
            }
        },
        [localidades]
    );

    useEffect(() => {
        if (!localidades || !selectedMethod || !isBoletoMethod(selectedMethod.tipoMetodo)) return;
        const uf = holderState.trim().toUpperCase();
        if (uf.length !== 2) return;
        loadBoletoCitiesForUf(uf, holderCity || undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- recarrega municípios quando a Open API concluir
    }, [localidades]);

    const loadCitiesForUf = useCallback(async (uf: string, preferredCity?: string) => {
        const sigla = uf.trim().toUpperCase();
        if (sigla.length !== 2) {
            setCityOptions([]);
            return;
        }

        setLoadingCities(true);
        try {
            let cities = await fetchCitiesByUf(sigla);
            if (preferredCity?.trim()) {
                cities = ensureCityInOptions(preferredCity, cities);
                const match = findCityInList(preferredCity, cities) ?? preferredCity.trim();
                setHolderCity(match);
            }
            setCityOptions(cities);
            clearFieldError('holderCity');
        } catch {
            if (preferredCity?.trim()) {
                setCityOptions([preferredCity.trim()]);
                setHolderCity(preferredCity.trim());
            } else {
                setCityOptions([]);
            }
        } finally {
            setLoadingCities(false);
        }
    }, []);

    const applyBoletoCepAddress = useCallback(
        async (address: { street: string; neighborhood: string; city: string; state: string }) => {
            const uf = address.state.trim().toUpperCase();
            setHolderAddress(address.street);
            setHolderNeighborhood(address.neighborhood);
            setHolderState(uf);
            setHolderCity('');
            setBoletoCityOptions([]);
            setErrors((prev) => {
                const next = { ...prev };
                delete next.holderCep;
                delete next.holderAddress;
                delete next.holderNeighborhood;
                delete next.holderCity;
                delete next.holderState;
                return next;
            });
            await loadBoletoCitiesForUf(uf, address.city);
        },
        [loadBoletoCitiesForUf]
    );

    const applyCepAddress = useCallback(
        async (address: { street: string; neighborhood: string; city: string; state: string }) => {
            const uf = address.state.trim().toUpperCase();
            setHolderAddress(address.street);
            setHolderNeighborhood(address.neighborhood);
            setHolderState(uf);
            setErrors((prev) => {
                const next = { ...prev };
                delete next.holderCep;
                delete next.holderAddress;
                delete next.holderNeighborhood;
                delete next.holderCity;
                delete next.holderState;
                return next;
            });
            await loadCitiesForUf(uf, address.city);
        },
        [loadCitiesForUf]
    );

    const lookupCep = useCallback(
        async (cepValue: string, useBoletoFlow = false) => {
            const cepDigits = cepValue.replace(/\D/g, '');
            if (cepDigits.length !== 8) return;
            if (lastCepFetchedRef.current === cepDigits) return;

            setLoadingCep(true);
            try {
                const address = await fetchAddressByCep(cepDigits);
                lastCepFetchedRef.current = cepDigits;
                if (useBoletoFlow) {
                    await applyBoletoCepAddress(address);
                } else {
                    await applyCepAddress(address);
                }
            } catch {
                lastCepFetchedRef.current = '';
                setErrors((prev) => ({ ...prev, holderCep: 'CEP não encontrado.' }));
            } finally {
                setLoadingCep(false);
            }
        },
        [applyCepAddress, applyBoletoCepAddress]
    );

    const handleHolderFieldChange = (name: string, value: string, boletoFlow = false) => {
        clearFieldError(name);
        if (name === 'holderCep') {
            setHolderCep(value);
            const cepDigits = value.replace(/\D/g, '');
            if (cepDigits.length < 8) {
                lastCepFetchedRef.current = '';
            }
            if (cepDigits.length === 8) {
                lookupCep(value, boletoFlow);
            }
        } else if (name === 'holderAddress') setHolderAddress(value);
        else if (name === 'holderNumber') setHolderNumber(value);
        else if (name === 'holderComplement') setHolderComplement(value);
        else if (name === 'holderNeighborhood') setHolderNeighborhood(value);
        else if (name === 'holderState') {
            const uf = value.toUpperCase();
            setHolderState(uf);
            setHolderCity('');
            if (boletoFlow) {
                setBoletoCityOptions([]);
                if (uf.length === 2) {
                    loadBoletoCitiesForUf(uf);
                }
            } else {
                setCityOptions([]);
                if (uf.length === 2) {
                    loadCitiesForUf(uf);
                }
            }
        } else if (name === 'holderCity') setHolderCity(value);
        else if (name === 'holderPhone') setHolderPhone(value);
    };

    const handleCepBlur = (boletoFlow = false) => {
        lookupCep(holderCep, boletoFlow);
    };

    const validateForm = (): boolean => {
        if (!selectedMethod) return false;

        const newErrors = isCreditCardMethod(selectedMethod.tipoMetodo)
            ? validateCreditCardCheckoutForm(
                  {
                      cardNumber,
                      cardHolderName,
                      cardExpiry,
                      cardCvv,
                      installments,
                      pagadorCpfCnpj,
                      pagadorNome,
                      emailContato,
                      holderCep,
                      holderAddress,
                      holderNumber,
                      holderNeighborhood,
                      holderState,
                      holderCity,
                      holderPhone,
                  },
                  selectedMethod,
                  { skipCardFields: HOSTED_CARD_MODE }
              )
            : isBoletoMethod(selectedMethod.tipoMetodo)
              ? validateBoletoCheckoutForm(
                    {
                        pagadorCpfCnpj,
                        pagadorNome,
                        emailContato,
                        holderPhone,
                        installments,
                        holderCep,
                        holderAddress,
                        holderNumber,
                        holderComplement,
                        holderNeighborhood,
                        holderState,
                        holderCity,
                    },
                    selectedMethod
                )
              : validatePixPagadorForm(pagadorCpfCnpj, pagadorNome, emailContato);

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    
    const calculateFinalValue = (method: PaymentMethod | null, baseValue: number): number => {
        if (!method) return baseValue;
        let finalValue = baseValue;
        if (method.descontoValor) {
            finalValue -= parseFloat(method.descontoValor);
        } else if (method.descontoPercentual) {
            finalValue -= (baseValue * parseFloat(method.descontoPercentual)) / 100;
        }
        if (method.taxaFixa) {
            finalValue += parseFloat(method.taxaFixa);
        } else if (method.taxaPercentual) {
            finalValue += (baseValue * parseFloat(method.taxaPercentual)) / 100;
        }
        return Math.max(finalValue, 0);
    };

    const handlePayment = async () => {
        // Verifique se o limite foi atingido antes de processar o pagamento
        if (isLimitReached) {
            toast.error("Este link de pagamento atingiu seu limite de usos.");
            return;
        }

        if (!selectedMethod || !linkData) return;

        const isFormValid = validateForm();
        if (!isFormValid) {
            toast.error("Por favor, corrija os erros no formulário.");
            return;
        }

        if (!empresaData) {
            toast.error("Os dados da empresa ainda não foram carregados. Tente novamente.");
            setProcessingPayment(false);
            return;
        }

        setProcessingPayment(true);
        setError(null);

        if (selectedMethod.tipoMetodo === 'pix') {
            try {
                const params = new URLSearchParams(window.location.search);
                const authToken = params.get('authToken') ?? '';
                const empresa = params.get('empresa');
                const filial = params.get('filial');

                const linkId = String(linkData.linkPagamentoId ?? '');
                const pixPayload = {
                    valor: valorFinal,
                    expiracao: selectedMethod.prazoExpiracaoMinutos
                        ? selectedMethod.prazoExpiracaoMinutos * 60
                        : 10800,
                };

                const response = await criarPix(linkId, pixPayload);

                if (response && response.data) {
                    const pixInfo = response.data;
                    
                    try {
                        const dataDeExpiracao = new Date(Date.now() + pixInfo.expiracao * 1000);
                        const historyPayload = {
                            linkPagamentoId: linkData.linkPagamentoId,
                            valorPago: valorFinal,
                            valorOriginal: valorBase,
                            metodoPagamentoUtilizado: selectedMethod.tipoMetodo,
                            statusPagamento: "pendente",
                            transacaoId: pixInfo.criptographyTx_id,
                            dataExpiracao: dataDeExpiracao.toISOString(),
                            codigoAutorizacao: null,
                            nomePagador: pagadorNome || null,
                            documentoPagador: pagadorCpfCnpj.replace(/[^\d]/g, '') || null,
                            emailPagador: emailContato || null,
                            dataPagamento: new Date().toISOString(),
                            observacoes: "Geração de QR Code PIX para pagamento."
                        };
                        
                        await createPaymentLinkHistory(authToken, historyPayload, empresa, filial);

                    } catch (historyError) {
                        console.error("Falha ao criar o registro de histórico do pagamento:", historyError);
                        toast.warn("Não foi possível registrar o histórico desta transação, mas o PIX foi gerado.");
                    }

                    const copiaECola = pixInfo?.copiaECola || pixInfo?.qrcode_url || '';
                    if (!copiaECola) {
                        throw new Error('PIX gerado sem código copia e cola.');
                    }

                    setPixData({
                        qrCode: copiaECola,
                        copiaECola,
                    });
                    if (pixInfo.id) {
                        setPixTransactionId(pixInfo.id);
                    }
                    if (pixInfo.stream_url) {
                        setPixStreamUrl(pixInfo.stream_url);
                    }

                    setIsPixModalOpen(true);

                } else {
                    setError('Não foi possível gerar o PIX. Resposta inválida da API.');
                }

            } catch (err: any) {
                console.error("Erro ao gerar PIX:", err);
                setError(err.message || 'Ocorreu um erro de comunicação com o servidor.');
            } finally {
                setProcessingPayment(false);
            }

        } else if (isCreditCardMethod(selectedMethod.tipoMetodo)) {
            try {
                const params = new URLSearchParams(window.location.search);
                const empresa = params.get('empresa');
                const filial = params.get('filial');

                const cardForm = {
                    cardNumber,
                    cardHolderName,
                    cardExpiry,
                    cardCvv,
                    installments,
                    pagadorCpfCnpj,
                    pagadorNome,
                    emailContato,
                    holderCep,
                    holderAddress,
                    holderNumber,
                    holderNeighborhood,
                    holderState,
                    holderCity,
                    holderPhone,
                };

                // Fallback HOSTED: o backend cria o checkout no C6 e devolve a URL da
                // página de pagamento do banco — redireciona a MESMA janela para lá.
                // Nada de histórico pelo front (o backend registra).
                const irParaCheckoutHosted = async () => {
                    const hosted = await criarCheckoutHosted(
                        {
                            linkPagamentoId: String(linkData.linkPagamentoId),
                            qtdParcelas: installments,
                            pagador: buildPagadorCartao(cardForm),
                            redirectUrl: window.location.href,
                        },
                        empresa,
                        filial
                    );
                    window.location.assign(hosted.url);
                };

                if (HOSTED_CARD_MODE) {
                    await irParaCheckoutHosted();
                    return;
                }

                const submission = buildCreditCardSubmission(String(linkData.linkPagamentoId), cardForm);

                // O histórico do pagamento de cartão é registrado pelo BACKEND durante o
                // authorize — o front não cria histórico neste fluxo.
                let resultado;
                try {
                    resultado = await processarCartao(submission, empresa, filial);
                } catch (sdkError: any) {
                    // SDK transparente indisponível em runtime (script falhou ao carregar,
                    // Web Crypto ausente) → cai no checkout hosted do C6.
                    if (sdkError instanceof C6SdkUnavailableError) {
                        await irParaCheckoutHosted();
                        return;
                    }
                    throw sdkError;
                }

                if (resultado.status === 'pago' || resultado.status === 'autorizado') {
                    setCardPaymentSuccess({
                        valorPago: valorFinal,
                        parcelas: submission.qtdParcelas,
                        codigoAutorizacao: resultado.codigoAutorizacao,
                        cartao: resultado.cartao,
                    });
                } else if (resultado.status === 'recusado') {
                    toast.error(
                        resultado.mensagem ||
                            'Pagamento não autorizado pelo emissor do cartão. Verifique os dados informados ou tente outro cartão.'
                    );
                } else if (resultado.status === 'pendente') {
                    toast.info(
                        resultado.mensagem ||
                            'Pagamento em processamento. Aguarde a confirmação — você será avisado pelo e-mail informado.'
                    );
                } else {
                    toast.error(
                        resultado.mensagem ||
                            'Não foi possível processar o pagamento no momento. Tente novamente em instantes.'
                    );
                }
            } catch (err: any) {
                console.error('Erro ao processar cartão:', err);
                toast.error(err?.message || 'Não foi possível processar o pagamento com cartão.');
            } finally {
                setProcessingPayment(false);
            }

        } else if (isBoletoMethod(selectedMethod.tipoMetodo)) {
            try {
                const params = new URLSearchParams(window.location.search);
                const authToken = params.get('authToken') ?? '';
                const empresa = params.get('empresa');
                const filial = params.get('filial');

                const payload = buildBoletoPayload(valorFinal, selectedMethod, {
                    pagadorCpfCnpj,
                    pagadorNome,
                    emailContato,
                    holderPhone,
                    installments,
                    holderCep,
                    holderAddress,
                    holderNumber,
                    holderComplement,
                    holderNeighborhood,
                    holderState,
                    holderCity,
                });

                const result = await emitirBoleto(linkData.linkPagamentoId, payload);

                if (result.erros?.length) {
                    const aviso = result.erros
                        .map((e) => (e.parcela != null ? `Parcela ${e.parcela}: ${e.motivo}` : e.motivo))
                        .join(' | ');
                    toast.warn(aviso);
                }

                const primeiroTitulo = result.titulos[0];
                const linhas = result.titulos.map((t) => t.linha_digitavel).filter(Boolean).join(' | ');

                try {
                    const historyPayload = {
                        linkPagamentoId: linkData.linkPagamentoId,
                        valorPago: valorFinal,
                        valorOriginal: valorBase,
                        metodoPagamentoUtilizado: selectedMethod.tipoMetodo,
                        statusPagamento: 'pendente',
                        transacaoId: primeiroTitulo.titulo_id,
                        dataExpiracao: null,
                        codigoAutorizacao: primeiroTitulo.nosso_numero || null,
                        nomePagador: payload.pagador.nome,
                        documentoPagador: payload.pagador['cpf-cnpj'],
                        emailPagador: payload.pagador.email,
                        dataPagamento: new Date().toISOString(),
                        observacoes: `Boleto emitido ${payload.parcelas}x. Linha digitável: ${linhas}`,
                    };

                    await createPaymentLinkHistory(authToken, historyPayload, empresa, filial);
                } catch (historyError) {
                    console.error('Falha ao criar histórico do boleto:', historyError);
                    toast.warn('Boleto gerado, mas não foi possível registrar o histórico.');
                }

                setBoletoTitulos(result.titulos);
                setIsBoletoModalOpen(true);
                toast.success('Boleto gerado com sucesso.');
            } catch (err: any) {
                console.error('Erro ao gerar boleto:', err);
                setError(err?.message || 'Não foi possível gerar o boleto.');
            } finally {
                setProcessingPayment(false);
            }
        } else {
            toast.warning(`Processamento para ${getMethodName(selectedMethod.tipoMetodo)} ainda não implementado.`);
            setProcessingPayment(false);
        }
    };

    useEffect(() => {
        if (pixData) {
            console.log("pixData atualizado:", pixData);
        }
    }, [pixData]);

    const boletoEstados = useMemo(() => {
        if (localidades?.estados?.length) return localidades.estados;
        return BRAZILIAN_STATES.map((s) => ({
            estadoId: 0,
            sigla: s.uf,
            nome: s.name,
        }));
    }, [localidades]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-2xl shadow-md flex items-center space-x-3 text-sky-600">
                    <FaSpinner className="animate-spin h-5 w-5" />
                    <span className="text-base font-medium">Carregando dados do pagamento...</span>
                </div>
            </div>
        );
    }

    if (isExpired) { //EXPIRADO
        return (
            <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4 text-center border border-red-200">
                    <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-3" />
                    <h2 className="text-lg font-bold text-gray-600 mb-2">Link Expirado</h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Este link de pagamento não é mais válido pois sua data de validade foi atingida.
                    </p>
                    <p className="text-xs text-gray-400">
                        Entre em contato com o emissor para gerar um novo link.
                    </p>
                </div>
            </div>
        );
    }

    if (isLimitReached) { //LIMITE USO ATINGIDO
        return (
            <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4 text-center border border-yellow-200">
                    <AlertCircle className="w-10 h-10 mx-auto text-yellow-500 mb-3" />
                    <h2 className="text-lg font-bold text-gray-600 mb-2">Limite de Usos Atingido</h2>
                    <p className="text-sm text-gray-600 mb-4">Este link de pagamento já foi utilizado a quantidade máxima de vezes.</p>
                    <p className="text-xs text-gray-400">Entre em contato com o emissor para mais informações.</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 flex items-center justify-center">
                <div className="bg-white p-6 rounded-2xl shadow-xl max-w-md w-full mx-4 text-center border border-red-200">
                    <AlertCircle className="w-10 h-10 mx-auto text-red-500 mb-3" />
                    <h2 className="text-lg font-bold text-gray-600 mb-2">Ocorreu um Erro</h2>
                    <p className="text-sm text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="w-full bg-sky-500 text-white py-2.5 px-4 rounded-xl hover:bg-sky-600 transition-all font-medium text-sm"
                    >
                        Tentar Novamente
                    </button>
                </div>
            </div>
        );
    }

    if (!linkData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-50 flex items-center justify-center">
                 <p className="text-gray-600 bg-white p-4 rounded-xl shadow-md text-sm">Dados do link não encontrados.</p>
            </div>
        );
    }

    const valorBase = parseFloat(linkData.valor);
    const valorFinal = calculateFinalValue(selectedMethod, valorBase);

    const getButtonText = () => {
        if (!selectedMethod) return 'Selecione um método';
        switch (selectedMethod.tipoMetodo) {
            case 'pix': return 'Gerar QR Code';
            case 'boleto': return 'Gerar Boleto';
            default: return HOSTED_CARD_MODE ? 'Continuar para o pagamento' : 'Pagar com Cartão';
        }
    };

    const isCardSelected =
        selectedMethod !== null && isCreditCardMethod(selectedMethod.tipoMetodo);

    const isBoletoSelected =
        selectedMethod !== null && isBoletoMethod(selectedMethod.tipoMetodo);

    return (
        <>
            {isPixModalOpen && pixData && (
                <PixModal
                    isOpen={isPixModalOpen}
                    onClose={() => setIsPixModalOpen(false)}
                    pixData={pixData}
                    finalValue={valorFinal}
                    expirationMinutes={selectedMethod?.prazoExpiracaoMinutos ?? null}
                    linkExpiration={linkData.validade}
                    transactionId={pixTransactionId}
                    streamUrl={pixStreamUrl}
                />
            )}

            {cardPaymentSuccess && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 sm:p-8 relative flex flex-col max-h-[90vh] overflow-y-auto text-center">
                        <div className="w-20 h-20 bg-emerald-100 rounded-full mx-auto flex items-center justify-center border-4 border-emerald-200">
                            <FaCheck className="w-10 h-10 text-emerald-500" />
                        </div>
                        <h2 className="text-2xl font-extrabold text-gray-800 mt-6">Pagamento Aprovado!</h2>
                        <p className="text-gray-600 mt-2">
                            Seu pagamento de{' '}
                            <span className="font-bold text-emerald-600">{formatCurrency(cardPaymentSuccess.valorPago)}</span>
                            {cardPaymentSuccess.parcelas > 1 ? ` em ${cardPaymentSuccess.parcelas}x` : ''} foi confirmado com sucesso.
                        </p>
                        {cardPaymentSuccess.cartao && (
                            <p className="text-sm text-gray-500 mt-3">
                                {cardPaymentSuccess.cartao.bandeira} • {cardPaymentSuccess.cartao.numeroMascarado}
                            </p>
                        )}
                        {cardPaymentSuccess.codigoAutorizacao && (
                            <p className="text-xs text-gray-400 mt-1">
                                Código de autorização: {cardPaymentSuccess.codigoAutorizacao}
                            </p>
                        )}
                        <button
                            onClick={() => setCardPaymentSuccess(null)}
                            className="mt-8 w-full bg-sky-500 text-white py-3 px-4 rounded-xl hover:bg-sky-600 transition-all font-bold"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            )}

            {isBoletoModalOpen && boletoTitulos.length > 0 && (
                <BoletoModal
                    isOpen={isBoletoModalOpen}
                    onClose={() => setIsBoletoModalOpen(false)}
                    titulos={boletoTitulos}
                    finalValue={valorFinal}
                />
            )}

            <div className="min-h-screen bg-gray-100 font-sans">
            <main className="max-w-6xl mx-auto py-6 lg:py-12 px-4">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <img
                        src={logoUrl || '/imagens/nexxusone-logo-light.png'}
                        alt="NexxusOne"
                        className="h-14 w-auto max-w-[200px] object-contain"
                    />
                </div>
                
                <div className="flex flex-col lg:flex-row gap-8">
                {/* Coluna Esquerda: Detalhes e Métodos de Pagamento */}
                <div className="w-full lg:w-2/3 space-y-2 min-w-0">
                    
                    {/* Informações do Link */}
                    <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 backdrop-blur-sm">
                    {/* Header com gradiente sutil */}
                    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
                        <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <LinkIcon className="w-4 h-4 text-white" />
                        </div>
                        <h1 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                        Informações do Link
                        </h1>
                    </div>

                    {/* Content com melhor espaçamento */}
                    <div className="space-y-2">
                        {empresaData && (
                            <div className="group flex items-center gap-3 rounded-xl hover:bg-sky-50/50 transition-colors duration-200">
                                <div className="min-w-0">
                                    <span className="text-sm font-medium text-gray-500 block">Recebedor</span>
                                    <span className="text-base font-bold text-gray-800 truncate block">
                                        {empresaData.nomeRazaoSocial}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Nome Link */}
                        <div className="group flex items-center gap-3 rounded-xl hover:bg-sky-50/50 transition-colors duration-200">
                            <div className=" min-w-0">
                                <span className="text-sm font-medium text-gray-500 block">Nome do Link</span>
                                <span className="text-base font-semibold text-gray-700 truncate block">{linkData.nomeLink}</span>
                            </div>
                        </div>

                        {/* Validade */}
                        <div className="group flex items-center gap-3 rounded-xl hover:bg-sky-50/50 transition-colors duration-200">
                            <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-gray-500 block">Válido até</span>
                                <span className="text-base font-semibold text-gray-700 block">{linkData.validade ? formatDate(linkData.validade, true) : 'Sem expiração'}</span>
                            </div>
                        </div>

                        {/* Descrição */}
                        <div className="group rounded-xl hover:shadow-sm transition-all duration-200 min-w-0 w-full">
                            <div className="flex items-start gap-3 mb-1">
                                <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium text-gray-500">Produto / Serviço</span>
                                </div>
                            </div>
                            <div className="min-w-0 w-full">
                                <div className="bg-white/80 backdrop-blur-sm rounded-lg border border-slate-200/50 p-4 shadow-sm min-w-0">
                                    <p className="text-gray-700 leading-relaxed text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                                        {linkData.descricaoProdutoServico || (
                                            <span className="text-gray-400 italic">Nenhuma descrição fornecida.</span>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    </div>

                    {/* Métodos de Pagamento */}
                    <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 backdrop-blur-sm">
                        <div className="flex items-center gap-3 mb-6 pb-3 border-b border-gray-100">
                            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
                            <CreditCard className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                        Escolha a forma de pagamento
                        </h2>
                    </div>
                    
                    <div className="space-y-2">
                        {paymentMethods.map((method) => {
                        const isSelected = selectedMethod?.metodoPagamentoId === method.metodoPagamentoId;
                        return (
                            <div
                            key={method.metodoPagamentoId}
                            onClick={() => setSelectedMethod(method)}
                            className={`group flex items-center justify-between p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-[1.02] ${
                                isSelected 
                                ? 'border-emerald-400 bg-gradient-to-r from-emerald-50 to-green-50 ring-2 ring-emerald-200/50 shadow-md' 
                                : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/30 hover:shadow-md'
                            }`}
                            >
                            <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                                isSelected 
                                    ? 'bg-emerald-100 text-emerald-600' 
                                    : 'bg-gray-100 text-gray-600 group-hover:bg-emerald-100 group-hover:text-emerald-600'
                                }`}>
                                {getMethodIcon(method.tipoMetodo)}
                                </div>
                                <div>
                                <h3 className="font-bold text-gray-700 text-base">{getMethodName(method.tipoMetodo)}</h3>
                                {(method.descontoPercentual || method.descontoValor) && (
                                    <div className="flex items-center gap-2 mt-2">
                                    <span className="text-xs font-bold text-emerald-700 bg-gradient-to-r from-emerald-100 to-green-100 px-3 py-1 rounded-full border border-emerald-200">
                                        <Zap className="w-3 h-3 inline mr-1" />
                                        {method.descontoPercentual ? `${method.descontoPercentual}% OFF` : 'Desconto especial'}
                                    </span>
                                    </div>
                                )}
                                </div>
                            </div>
                            <div className="text-right">
                                {(method.descontoPercentual || method.descontoValor) ? (
                                <div className="space-y-1">
                                    <div className="text-sm text-gray-400 line-through">
                                    {formatCurrency(valorBase)}
                                    </div>
                                    <div className="font-bold text-emerald-600 text-lg">
                                    {formatCurrency(calculateFinalValue(method, valorBase))}
                                    </div>
                                </div>
                                ) : (
                                <div className="font-bold text-gray-700 text-lg">
                                    {formatCurrency(calculateFinalValue(method, valorBase))}
                                </div>
                                )}
                            </div>
                            </div>
                        );
                        })}
                    </div>
                    </div>

                    {isCardSelected && selectedMethod && (
                        <CreditCardFields
                            method={selectedMethod}
                            finalValue={valorFinal}
                            values={{
                                cardNumber,
                                cardHolderName,
                                cardExpiry,
                                cardCvv,
                                installments,
                            }}
                            errors={errors}
                            onChange={handleCardFieldChange}
                            hostedMode={HOSTED_CARD_MODE}
                        />
                    )}

                    {isBoletoSelected && selectedMethod && (
                        <BoletoFields
                            method={selectedMethod}
                            finalValue={valorFinal}
                            values={{ installments }}
                            errors={errors}
                            onChange={handleCardFieldChange}
                        />
                    )}

                    {/* Dados do Pagador / Titular do cartão / Boleto */}
                    <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 backdrop-blur-sm">
                        <div className="flex items-center gap-3 mb-4 pb-1 border-b border-gray-100">
                            <div className="w-8 h-8 bg-gradient-to-br from-slate-500 to-gray-600 rounded-lg flex items-center justify-center">
                            <User className="w-4 h-4 text-white" />
                            </div>
                            <div className="flex-1">
                            <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                {isCardSelected
                                    ? 'Titular do Cartão'
                                    : isBoletoSelected
                                      ? 'Dados do Pagador'
                                      : 'Dados do Pagador'}
                            </h3>
                            {!isCardSelected && !isBoletoSelected && (
                                <span className="text-sm text-gray-500">(Opcional)</span>
                            )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="group">
                                {isCardSelected ? (
                                    <>
                                        <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
                                            CPF <span className="text-red-500">*</span>
                                        </label>
                                        <IMaskInput
                                            mask="000.000.000-00"
                                            value={pagadorCpfCnpj}
                                            onAccept={(val: string) => {
                                                clearFieldError('pagadorCpfCnpj');
                                                setPagadorCpfCnpj(val);
                                            }}
                                            placeholder="000.000.000-00"
                                            className={`w-full px-4 mt-1 py-2 h-[42px] rounded-lg border bg-white ${
                                                errors.pagadorCpfCnpj ? 'border-red-500' : 'border-gray-300'
                                            } focus:outline-none focus:ring-1 focus:ring-blue-300`}
                                        />
                                        {errors.pagadorCpfCnpj && (
                                            <p className="text-red-500 text-sm mt-1">{errors.pagadorCpfCnpj}</p>
                                        )}
                                    </>
                                ) : (
                                    <MaskedInputCPF
                                        label="CPF / CNPJ"
                                        name="pagadorCpfCnpj"
                                        value={pagadorCpfCnpj}
                                        onChange={handleInputChange}
                                        errorMessage={errors.pagadorCpfCnpj}
                                        placeholder="Digite o documento"
                                        requiredMark={isBoletoSelected}
                                        labelSize="sm"
                                        labelColor="gray-600"
                                        labelWeight="medium"
                                    />
                                )}
                            </div>
                            <div className="group">
                                <TextInput
                                    name="pagadorNome"
                                    label={isCardSelected || isBoletoSelected ? 'Nome Completo' : 'Nome'}
                                    value={pagadorNome}
                                    onChange={handleInputChange}
                                    errorMessage={errors.pagadorNome}
                                    placeholder="Digite o nome completo"
                                    requiredMark={isCardSelected || isBoletoSelected}
                                    maxLength={CHECKOUT_FIELD_LIMITS.pagadorNome}
                                    labelSize="sm"
                                    labelColor="gray-600"
                                    labelWeight="medium"
                                />
                            </div>
                        </div>
                        <div
                            className={`grid grid-cols-1 gap-4 mt-4 ${
                                isCardSelected || isBoletoSelected ? 'md:grid-cols-2' : 'md:grid-cols-1'
                            }`}
                        >
                            <TextInput
                                name="emailContato"
                                label="E-mail para Contato"
                                type="email"
                                value={emailContato}
                                onChange={handleInputChange}
                                errorMessage={errors.emailContato}
                                placeholder="exemplo@dominio.com"
                                requiredMark={isCardSelected || isBoletoSelected}
                                maxLength={CHECKOUT_FIELD_LIMITS.emailContato}
                                labelSize="sm"
                                labelColor="gray-600"
                                labelWeight="medium"
                            />
                            {(isCardSelected || isBoletoSelected) && (
                                <div>
                                    <label className="text-sm font-medium text-gray-600 flex items-center gap-1">
                                        Celular para Contato <span className="text-red-500">*</span>
                                    </label>
                                    <IMaskInput
                                        mask="(00) 00000-0000"
                                        value={holderPhone}
                                        onAccept={(val: string) =>
                                            handleHolderFieldChange('holderPhone', val, isBoletoSelected)
                                        }
                                        placeholder="(00) 00000-0000"
                                        className={`w-full px-4 mt-1 py-2 h-[42px] rounded-lg border bg-white ${
                                            errors.holderPhone ? 'border-red-500' : 'border-gray-300'
                                        } focus:outline-none focus:ring-1 focus:ring-blue-300`}
                                    />
                                    {errors.holderPhone && (
                                        <p className="text-red-500 text-sm mt-1">{errors.holderPhone}</p>
                                    )}
                                </div>
                            )}
                        </div>

                        {isCardSelected && (
                            <CardHolderFields
                                values={{
                                    holderCep,
                                    holderAddress,
                                    holderNumber,
                                    holderNeighborhood,
                                    holderState,
                                    holderCity,
                                }}
                                errors={errors}
                                onChange={(name, value) => handleHolderFieldChange(name, value, false)}
                                onCepBlur={() => handleCepBlur(false)}
                                loadingCep={loadingCep}
                                cityOptions={cityOptions}
                                loadingCities={loadingCities}
                            />
                        )}

                        {isBoletoSelected && (
                            <BoletoHolderFields
                                values={{
                                    holderCep,
                                    holderAddress,
                                    holderNumber,
                                    holderComplement,
                                    holderNeighborhood,
                                    holderState,
                                    holderCity,
                                }}
                                errors={errors}
                                onChange={(name, value) => handleHolderFieldChange(name, value, true)}
                                onCepBlur={() => handleCepBlur(true)}
                                loadingCep={loadingCep}
                                estados={boletoEstados}
                                cityOptions={boletoCityOptions}
                                loadingCities={loadingBoletoCities}
                                loadingLocalidades={loadingLocalidades}
                            />
                        )}
                    </div>
                </div>

                {/* Coluna Direita: Resumo do Pedido */}
                <div className="w-full lg:w-1/3">
                    <div className="bg-gradient-to-br from-white to-sky-50/30 p-6 rounded-2xl shadow-md lg:sticky lg:top-8 border border-sky-100/60 backdrop-blur-sm">
                    <div className="flex items-center gap-3 mb-6 pb-3 border-b border-gray-100">
                        <div className="w-8 h-8 bg-gradient-to-br from-sky-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <Receipt className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                        Resumo do Pedido
                        </h2>
                    </div>
                    
                    <div className="space-y-4 text-gray-600">
                        <div className="flex justify-between items-center p-3 bg-slate-50/50 rounded-lg">
                        <span className="font-medium">Valor original</span>
                        <span className="font-bold text-gray-700">{formatCurrency(valorBase)}</span>
                        </div>
                        
                        {selectedMethod?.descontoPercentual && (
                        <div className="flex justify-between items-center p-3 bg-gradient-to-r from-emerald-50 to-green-50 rounded-lg border border-emerald-200/50">
                            <span className="font-medium text-emerald-700">Desconto ({selectedMethod.descontoPercentual}%)</span>
                            <span className="font-bold text-emerald-600">
                            -{formatCurrency((valorBase * parseFloat(selectedMethod.descontoPercentual)) / 100)}
                            </span>
                        </div>
                        )}
                        
                        <div className="border-t border-sky-100 pt-4 mt-6">
                            <div className="flex justify-between items-center p-2 bg-gradient-to-r from-sky-50 to-blue-50 rounded-xl border border-sky-200/50">
                                <span className="font-bold text-lg text-sky-700">Total</span>
                                <span className="font-bold text-lg text-sky-600">{formatCurrency(valorFinal)}</span>
                            </div>
                        </div>
                    </div>
                    
                    <button
                        onClick={handlePayment}
                        disabled={!selectedMethod || processingPayment || isLimitReached}
                        className="w-full mt-6 py-3 px-4 rounded-xl font-bold text-white bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 transition-all disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-base shadow-md hover:shadow-xl transform hover:scale-[1.02] disabled:transform-none"
                    >
                        {processingPayment ? (
                        <>
                            <FaSpinner className="animate-spin w-5 h-5" />
                            <span>Processando...</span>
                        </>
                        ) : (
                        <>
                            <Shield className="w-5 h-5" />
                            {getButtonText()}
                        </>
                        )}
                    </button>
                    
                    {/* Informação sobre processamento pela Chronos */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 backdrop-blur-sm">
                        <div className="flex items-start gap-3">
                            {/* Coluna 1 - Ícone */}
                            <div className="w-10 h-10 bg-gradient-to-br from-gray-500 to-slate-600 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Shield className="w-5 h-5 text-white" />
                            </div>
                            {/* Coluna 2 - Texto */}
                            <p className="text-sm text-gray-600 leading-relaxed">
                            Este pagamento será processado com segurança pela{" "}
                            <span className="font-bold text-gray-800">Chronos Informatica Ltda</span>, nossa plataforma
                            certificada e protegida.
                            </p>
                        </div>
                    </div>
                    </div>
                </div>
                </div>
            </main>
            </div>

        </>
    );
};

export default CheckoutPage;